import type { DemoDoc, Step } from './demo';

/**
 * The single linear path a video replay (MP4 export, social cut) walks
 * through a demo: `start` → `nextIdFor` → … → null. Moved here from the
 * pipeline's exporter (`services/pipeline/src/export/plan.ts`, which
 * re-exports both names unchanged for its existing callers/tests) because
 * the social-cut step picker (`./social-cut.ts`) needs to walk the exact
 * same path — a step picker that disagreed with the exporter about what
 * "the path" even is would silently pick from the wrong set of steps.
 */

/**
 * Same rule the live player uses to auto-advance with no click
 * (`apps/player/src/runtime.ts` `nextIdFor`): the step's own `next` wins,
 * otherwise the first hotspot that has one. Kept byte-for-byte identical on
 * purpose — a step's "auto-advance" target must not depend on which renderer
 * is asking.
 */
export function nextIdFor(step: Step): string | null {
  if (step.next) return step.next;
  const withTarget = step.hotspots.find((h) => h.next);
  return withTarget?.next ?? null;
}

/**
 * A video is one recording, not a choose-your-own-adventure, so branching
 * (several hotspots aiming at different steps) collapses to whichever `next`
 * resolves first — the same one a viewer gets by doing nothing at all. A demo
 * that cycles back on itself stops the moment a step would repeat, which is
 * what guarantees this always terminates.
 */
export function resolveLinearPath(doc: DemoDoc): Step[] {
  const byId = new Map(doc.steps.map((s) => [s.id, s]));
  const path: Step[] = [];
  const seen = new Set<string>();
  let cur: string | null = doc.start;
  while (cur && !seen.has(cur)) {
    const step = byId.get(cur);
    if (!step) break;
    path.push(step);
    seen.add(cur);
    cur = nextIdFor(step);
  }
  return path;
}
